import { ISelectOption } from 'Interfaces/Utils';
import { ICompany } from 'Models/Company';
import { TypeOf,  coerce, object, string, union, instanceof as zInstanceof, z } from 'zod';
import { addYears } from 'date-fns';
import { validateIdentityDocument } from 'utils/validateNif';

// Form component interface

export interface ICompanyFormProps {
  apiError: any;
  currentCompany?: ICompany;
  onSubmit: (formData: FormData) => void;
  provinces: ISelectOption<string>[];
  companyKey?: string;
};

// STATIC FORM VARS

// Form fields

export const FIELDS = {
  NAME: {
    FIELD: 'name',
    LABEL: 'company.name',
    ERROR: 'Nombre de la empresa obligatorio',
  },
  ADDRESS_TYPE: {
    FIELD: 'addressType',
    LABEL: 'company.addressType',
    ERROR: 'Tipo de vía obligatorio',
  },
  ADDRESS_NAME: {
      FIELD: 'addressName',
      LABEL: 'company.addressName',
      ERROR: 'Nombre de la vía obligatorio',
    },
  ADDRESS_NUMBER: {
      FIELD: 'addressNumber',
      LABEL: 'company.addressNumber',
      ERROR: 'Número obligatorio',
    },
  ADDRESS_DETAILS: {
      FIELD: 'addressDetails',
      LABEL: 'company.addressDetails',
      ERROR: 'Detalles adicionales obligatorios si aplican',
    },
  POSTAL_CODE: {
      FIELD: 'postalCode',
      LABEL: 'company.postalCode',
      ERROR: 'Código Postal obligatorio',
    },
  PROVINCE: {
      FIELD: 'province',
      LABEL: 'company.province',
      ERROR: 'Provincia obligatoria',
    },
  NIF:{
    FIELD: 'nif',
    LABEL: 'company.nif',
    ERROR: 'NIF obligatorio',
  },
  LOCALITY: {
    FIELD: 'locality',
    LABEL: 'company.locality',
    ERROR: 'Localidad obligatoria',
  },
  LIMIT: {
    FIELD: 'limit',
    LABEL: 'company.limit',
    ERROR: 'Umbral de gasto obligatorio',
  },
  KEY: {
    FIELD: 'key',
    LABEL: 'company.key'
  },
  IMAGE: {
    FIELD: 'image',
  },
  PHONE_NUMBER: {
    FIELD: 'phoneNumber',
    LABEL: 'company.phoneNumber',
    ERROR: 'Teléfono obligatorio',
  },
  EMAIL: {
    FIELD: 'email',
    LABEL: 'company.email',
    ERROR_REQUIRED: 'El email es obligatorio',
    ERROR_INVALID: 'Email inválido'
  },
  DESACTIVATED_AT: {
    FIELD: 'deactivatedAt',
    LABEL: 'company.deactivatedAt',
    ERROR_INVALID: 'La fecha de desactivación no es válida',
  },
  VERIFICATION_LIST: {
    FIELD: 'verificationList',
    LABEL: 'company.verificationList',
    ERROR: 'Lista de verificación obligatoria',
  },
  ACTIVE_PRESTASHOP:{
    FIELD: 'activePrestaShop',
    LABEL: 'company.prestaShop',
    ERROR: 'Campo obligatorio',
  },
 
};

// Form validation schema
const maxDate = addYears(new Date(), 50);
const minDate = new Date('2025-01-01');

export const companyFormSchema = object({
  [FIELDS.NAME.FIELD]: string().min(1, FIELDS.NAME.ERROR),
  [FIELDS.ADDRESS_TYPE.FIELD]: string().min(1, FIELDS.ADDRESS_TYPE.ERROR),
  [FIELDS.ADDRESS_NAME.FIELD]: string().min(1, FIELDS.ADDRESS_NAME.ERROR),
  [FIELDS.ADDRESS_NUMBER.FIELD]: string().min(1, FIELDS.ADDRESS_NUMBER.ERROR),
  [FIELDS.ADDRESS_DETAILS.FIELD]: string().optional(),
  [FIELDS.POSTAL_CODE.FIELD]: string().min(1, FIELDS.POSTAL_CODE.ERROR),
  [FIELDS.PROVINCE.FIELD]: string().min(1, FIELDS.PROVINCE.ERROR),
  [FIELDS.NIF.FIELD]: string()
    .min(1, FIELDS.NIF.ERROR)
    .refine((nif) => validateIdentityDocument(nif), {
      message: 'NIF inválido. Debe ser un DNI, CIF o NIE válido.',
    }),
  [FIELDS.LOCALITY.FIELD]: string().min(1, FIELDS.LOCALITY.ERROR),
  [FIELDS.IMAGE.FIELD]: union([string(), zInstanceof(File)]).optional(),
  [FIELDS.LIMIT.FIELD]: z.number().min(0, FIELDS.LIMIT.ERROR),
  [FIELDS.PHONE_NUMBER.FIELD]: string().min(1, FIELDS.PHONE_NUMBER.ERROR),
  [FIELDS.EMAIL.FIELD]: string().min(1, FIELDS.EMAIL.ERROR_REQUIRED).email(FIELDS.EMAIL.ERROR_INVALID),
  [FIELDS.DESACTIVATED_AT.FIELD]: z
    .union([
      z.literal('').optional(), // Permitir cadenas vacías
      z.null(), // Permitir valores nulos explícitos
      coerce
        .date({ errorMap: () => ({ message: FIELDS.DESACTIVATED_AT.ERROR_INVALID }) })
        .min(minDate, FIELDS.DESACTIVATED_AT.ERROR_INVALID)
        .max(maxDate, FIELDS.DESACTIVATED_AT.ERROR_INVALID),
    ])
    .optional(),
  [FIELDS.VERIFICATION_LIST.FIELD]: z.array(z.string()).min(1, "Debes seleccionar al menos un servicio"),
  [FIELDS.ACTIVE_PRESTASHOP.FIELD]: z.boolean().optional(),
});


// Form values interface

export type ICompanyForm = TypeOf<typeof companyFormSchema>;

// Default values function

export const initialValues = (company: ICompany | undefined, companyKey:string): ICompanyForm => {
  const companyForm: ICompanyForm = {
    name: company?.name || "",
    addressType: company?.addressType || "",
    addressName: company?.addressName || "",
    addressNumber: company?.addressNumber || "",
    addressDetails: company?.addressDetails || "",
    postalCode: company?.postalCode || "",
    province: company?.province || "",
    nif: company?.nif || "",
    locality: company?.locality || "",
    limit: company?.limit || 0,
    phoneNumber: company?.phoneNumber || "",
    email: company?.email || "",
    deactivatedAt: company?.deactivatedAt || undefined,
    key: companyKey || "",
    verificationList: company?.verificationList || [],
    activePrestaShop: company?.activePrestaShop || false,
  };

  return companyForm;
};